/**
 * ScanPagesPreview — Jul-2026 (User-Wunsch).
 * Mini-Vorschauen ALLER Seiten eines Scans (PDF mehrseitig oder Bild),
 * damit fehlende, doppelte oder schiefe Seiten sofort auffallen.
 * Klick auf eine Seite öffnet die Großansicht.
 */
import { useEffect, useState } from "react";
import { Loader2, X, Layers } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

const MAX_PAGES = 40;

async function renderPdfThumbs(buf, scaleWidth) {
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    const n = Math.min(pdf.numPages, MAX_PAGES);
    const out = [];
    for (let i = 1; i <= n; i++) {
        const page = await pdf.getPage(i);
        const vp1 = page.getViewport({ scale: 1 });
        const scale = scaleWidth / vp1.width;
        const vp = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = vp.width;
        canvas.height = vp.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
        out.push(canvas.toDataURL("image/jpeg", 0.75));
    }
    return { thumbs: out, total: pdf.numPages };
}

export const ScanPagesPreview = ({ file, testId = "scan-pages-preview" }) => {
    const [pages, setPages] = useState(null);
    const [total, setTotal] = useState(0);
    const [zoom, setZoom] = useState(null); // {src, index}
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setPages(null);
        setError(false);
        setZoom(null);
        if (!file) return undefined;
        (async () => {
            try {
                const isPdf = (file.type || "").includes("pdf") || /\.pdf$/i.test(file.name || "");
                if (isPdf) {
                    const buf = await file.arrayBuffer();
                    const smalls = await renderPdfThumbs(buf.slice(0), 180);
                    if (cancelled) return;
                    setPages(smalls.thumbs);
                    setTotal(smalls.total);
                } else if ((file.type || "").startsWith("image/")) {
                    const url = URL.createObjectURL(file);
                    if (cancelled) { URL.revokeObjectURL(url); return; }
                    setPages([url]);
                    setTotal(1);
                } else {
                    setPages([]);
                    setTotal(0);
                }
            } catch {
                if (!cancelled) setError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [file]);

    if (!file) return null;
    if (error) return null;

    return (
        <div className="mt-3 bg-white border border-gray-200 rounded-sm p-3" data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-600 inline-flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5" /> Seitenvorschau
                    {total > 0 && (
                        <span className="normal-case tracking-normal font-bold text-gray-800" data-testid={`${testId}-count`}>
                            — {total} Seite{total === 1 ? "" : "n"} erkannt{total > MAX_PAGES ? ` (erste ${MAX_PAGES} angezeigt)` : ""}
                        </span>
                    )}
                </div>
                <span className="text-[10px] text-gray-400">Klick = Großansicht · fehlende/schiefe Seiten? → neu scannen</span>
            </div>
            {pages === null ? (
                <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
                    <Loader2 className="w-4 h-4 animate-spin" /> Vorschau wird erstellt…
                </div>
            ) : (
                <div className="flex gap-2 overflow-x-auto pb-1">
                    {pages.map((src, i) => (
                        <button key={i} type="button" onClick={() => setZoom({ src, index: i })}
                            className="shrink-0 group text-center" data-testid={`${testId}-page-${i + 1}`}>
                            <img src={src} alt={`Seite ${i + 1}`}
                                className="h-32 w-auto border border-gray-300 rounded-sm shadow-sm group-hover:border-[#FFCC00] group-hover:shadow-md transition-shadow bg-white" />
                            <div className="text-[10px] text-gray-500 mt-0.5 font-mono-num">S. {i + 1}</div>
                        </button>
                    ))}
                </div>
            )}
            {zoom && (
                <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-6" data-testid={`${testId}-zoom`}>
                    <button type="button" className="absolute top-4 right-4 text-white bg-white/10 rounded-sm p-2 hover:bg-white/20" onClick={() => setZoom(null)} data-testid={`${testId}-zoom-close`}>
                        <X className="w-5 h-5" />
                    </button>
                    <div className="max-h-full overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <img src={zoom.src} alt={`Seite ${zoom.index + 1}`} className="max-w-[92vw] rounded-sm shadow-2xl bg-white" />
                        <div className="text-center text-white text-xs font-bold mt-2">Seite {zoom.index + 1} von {total}</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScanPagesPreview;
